import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ChevronLeft, Palette, Globe, Target, Sparkles, ShieldAlert, MessageSquare } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';


const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
      <Icon size={16} className="text-[#128C7E]" />
      <h3 className="text-sm font-bold text-slate-700">{title}</h3>
    </div>
    <div className="p-4 space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, placeholder, multiline, type = 'text' }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {multiline ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none resize-none" />
    ) : (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none" />
    )}
  </div>
);

const TagsField = ({ label, value, onChange, placeholder }) => {
  const [input, setInput] = useState('');
  const tags = value || [];
  const add = () => { if (input.trim()) { onChange([...tags, input.trim()]); setInput(''); } };
  const remove = (i) => onChange(tags.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder} className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#128C7E] outline-none" />
        <button onClick={add} className="px-3 py-2 bg-[#128C7E] text-white rounded-lg text-sm font-medium">Add</button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tags.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-[#128C7E] rounded-full text-xs font-medium">
              {t} <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600 ml-0.5">x</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const BrandBrainScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brand, setBrand] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchBrand(); }, []);

  const fetchBrand = async () => {
    try {
      const res = await api.get(`/brand-brain`);
      if (res.data.brand) setBrand(res.data.brand);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/brand-brain`, brand);
      setBrand(res.data.brand);
      toast.success('Brand Brain saved! All future AI content will reflect your brand.');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const u = (field) => (val) => setBrand(prev => ({ ...prev, [field]: val }));

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="Brand Brain" showBack={true} action={
          <button onClick={handleSave} disabled={saving} className="text-sm px-3 py-1.5 rounded-lg bg-white/20 font-medium flex items-center gap-1" data-testid="save-brand-btn">
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        } />

        <div className="p-4 space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
            <p className="text-xs text-purple-800 leading-relaxed">
              <strong>Brand Brain</strong> powers all AI-generated content. Set your brand identity once, and every product listing, social post, and ad will reflect your unique brand voice.
            </p>
          </div>

          <Section icon={Palette} title="Brand Identity">
            <Field label="Brand Name" value={brand.brand_name} onChange={u('brand_name')} placeholder="e.g. Asian Libaas" />
            <Field label="Website" value={brand.website} onChange={u('website')} placeholder="https://yourbrand.com" />
            <Field label="Brand Story" value={brand.brand_story} onChange={u('brand_story')} placeholder="Tell your brand's story in 2-3 sentences..." multiline />
            <Field label="Brand Description" value={brand.brand_description} onChange={u('brand_description')} placeholder="What does your brand do?" multiline />
          </Section>

          <Section icon={Palette} title="Brand Colors">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Primary" value={brand.primary_color} onChange={u('primary_color')} placeholder="#128C7E" />
              <Field label="Secondary" value={brand.secondary_color} onChange={u('secondary_color')} placeholder="#25D366" />
              <Field label="Accent" value={brand.accent_color} onChange={u('accent_color')} placeholder="#FFD700" />
            </div>
          </Section>

          <Section icon={MessageSquare} title="Brand Voice">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">Tone of Voice</label>
              <div className="flex flex-wrap gap-2">
                {['luxury', 'premium', 'casual', 'professional', 'friendly', 'bold', 'traditional', 'modern'].map(t => (
                  <button key={t} onClick={() => u('tone')(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${brand.tone === t ? 'bg-[#128C7E] text-white' : 'bg-gray-100 text-gray-600'}`}
                    data-testid={`tone-${t}`}
                  >{t}</button>
                ))}
              </div>
            </div>
            <Field label="Brand Personality" value={brand.brand_personality} onChange={u('brand_personality')} placeholder="e.g. Elegant yet approachable" />
          </Section>

          <Section icon={Target} title="Target Audience">
            <Field label="Who is your customer?" value={brand.target_audience} onChange={u('target_audience')} placeholder="e.g. Young Indian women aged 25-40" />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">Price Positioning</label>
              <div className="flex flex-wrap gap-2">
                {['budget', 'mid-range', 'premium', 'luxury'].map(p => (
                  <button key={p} onClick={() => u('price_positioning')(p)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${brand.price_positioning === p ? 'bg-[#128C7E] text-white' : 'bg-gray-100 text-gray-600'}`}
                  >{p}</button>
                ))}
              </div>
            </div>
          </Section>

          <Section icon={Sparkles} title="Brand Positioning">
            <TagsField label="Unique Selling Points (USPs)" value={brand.usps} onChange={u('usps')} placeholder="Add a USP" />
            <TagsField label="Competitors" value={brand.competitors} onChange={u('competitors')} placeholder="Add competitor name" />
            <Field label="Product Positioning" value={brand.product_positioning} onChange={u('product_positioning')} placeholder="e.g. Luxury Indian fashion rooted in heritage" multiline />
          </Section>

          <Section icon={ShieldAlert} title="Content Rules">
            <TagsField label="Words to USE in content" value={brand.words_to_use} onChange={u('words_to_use')} placeholder="e.g. premium, handcrafted" />
            <TagsField label="Words to AVOID" value={brand.words_to_avoid} onChange={u('words_to_avoid')} placeholder="e.g. cheap, discount" />
          </Section>

          <button onClick={handleSave} disabled={saving}
            className="w-full h-14 bg-[#128C7E] text-white rounded-xl text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="save-brand-bottom"
          >
            <Save size={20} /> {saving ? 'Saving Brand Brain...' : 'Save Brand Brain'}
          </button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
